import type {
  AxisCalibration,
  AxisDirection,
  AxisQuantity,
  AxisScale,
  ChartCalibration,
  CurveDraftDocument,
  CurveTraceDraft,
} from './types';
import { defaultCalibration, defaultChartRegion, updateTrace } from './editorState';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { curveImportFieldHelp } from '../../app/guidance/fieldHelpRegistry';

interface CalibrationEditorProps {
  activeTrace: CurveTraceDraft | null;
  document: CurveDraftDocument;
  onCommit: (document: CurveDraftDocument) => void;
}

interface AxisEditorProps {
  axis: AxisCalibration;
  axisName: 'Flow axis' | 'Vertical axis';
  pixelCoordinate: 'x' | 'y';
  lockQuantity?: boolean;
  onChange: (axis: AxisCalibration) => void;
}

const quantities: AxisQuantity[] = [
  'flow',
  'head',
  'efficiency',
  'power',
  'npshr',
  'suction_lift',
];

function AxisEditor({
  axis,
  axisName,
  pixelCoordinate,
  lockQuantity = false,
  onChange,
}: AxisEditorProps) {
  const changeNumber = (
    key: 'pixel_end' | 'pixel_start' | 'value_end' | 'value_start',
    value: string,
  ) => onChange({ ...axis, [key]: Number(value) });

  return (
    <fieldset className="curve-calibration__axis">
      <legend>{axisName}</legend>
      <label>
        Quantity
        <select {...guided(curveImportFieldHelp.axisQuantity, axisName)}
          aria-label={`${axisName} quantity`}
          disabled={lockQuantity}
          value={axis.quantity}
          onChange={(event) =>
            onChange({
              ...axis,
              quantity: event.target.value as AxisQuantity,
            })
          }
        >
          {quantities.map((quantity) => (
            <option key={quantity} value={quantity}>
              {quantity.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </label>
      <label>
        Unit
        <input {...guided(curveImportFieldHelp.axisUnit, axisName)}
          aria-label={`${axisName} unit`}
          value={axis.unit}
          onChange={(event) => onChange({ ...axis, unit: event.target.value })}
        />
      </label>
      <label>
        Scale
        <select {...guided(curveImportFieldHelp.axisScale, axisName)}
          aria-label={`${axisName} scale`}
          value={axis.scale}
          onChange={(event) =>
            onChange({ ...axis, scale: event.target.value as AxisScale })
          }
        >
          <option value="linear">Linear</option>
          <option value="log10">Logarithmic</option>
        </select>
      </label>
      <label>
        Value direction
        <select {...guided(curveImportFieldHelp.axisDirection, axisName)}
          aria-label={`${axisName} direction`}
          value={axis.direction}
          onChange={(event) =>
            onChange({
              ...axis,
              direction: event.target.value as AxisDirection,
            })
          }
        >
          <option value="increasing">Increasing</option>
          <option value="decreasing">Decreasing</option>
        </select>
      </label>
      <label>
        First tick {pixelCoordinate} pixel
        <input {...guided(curveImportFieldHelp.firstTickPixel, axisName)}
          aria-label={`${axisName} first tick ${pixelCoordinate} pixel`}
          type="number"
          step="any"
          value={axis.pixel_start}
          onChange={(event) => changeNumber('pixel_start', event.target.value)}
        />
      </label>
      <label>
        First tick value
        <input {...guided(curveImportFieldHelp.firstTickValue, axisName)}
          aria-label={`${axisName} first tick value`}
          type="number"
          step="any"
          value={axis.value_start}
          onChange={(event) => changeNumber('value_start', event.target.value)}
        />
      </label>
      <label>
        Second tick {pixelCoordinate} pixel
        <input {...guided(curveImportFieldHelp.secondTickPixel, axisName)}
          aria-label={`${axisName} second tick ${pixelCoordinate} pixel`}
          type="number"
          step="any"
          value={axis.pixel_end}
          onChange={(event) => changeNumber('pixel_end', event.target.value)}
        />
      </label>
      <label>
        Second tick value
        <input {...guided(curveImportFieldHelp.secondTickValue, axisName)}
          aria-label={`${axisName} second tick value`}
          type="number"
          step="any"
          value={axis.value_end}
          onChange={(event) => changeNumber('value_end', event.target.value)}
        />
      </label>
    </fieldset>
  );
}

export function CalibrationEditor({
  activeTrace,
  document,
  onCommit,
}: CalibrationEditorProps) {
  const usesOverride =
    activeTrace !== null && activeTrace.calibration_override !== null;
  const calibration = usesOverride
    ? (activeTrace?.calibration_override ?? null)
    : document.calibration;

  const createCalibration = () => {
    const region =
      activeTrace?.chart_region ??
      document.selected_chart ??
      defaultChartRegion(document.selected_page ?? 1, 1000, 750);
    const created = defaultCalibration(region);
    if (activeTrace !== null && usesOverride) {
      onCommit(
        updateTrace(document, activeTrace.id, (trace) => ({
          ...trace,
          calibration_override: created,
        })),
      );
      return;
    }
    onCommit({ ...document, calibration: created });
  };

  const commitCalibration = (next: ChartCalibration) => {
    if (activeTrace !== null && usesOverride) {
      onCommit(
        updateTrace(document, activeTrace.id, (trace) => ({
          ...trace,
          calibration_override: next,
        })),
      );
      return;
    }
    onCommit({ ...document, calibration: next });
  };

  const toggleOverride = (checked: boolean) => {
    if (activeTrace === null) return;
    onCommit(
      updateTrace(document, activeTrace.id, (trace) => ({
        ...trace,
        calibration_override: checked
          ? (document.calibration ??
            defaultCalibration(
              trace.chart_region ??
                document.selected_chart ??
                defaultChartRegion(document.selected_page ?? 1, 1000, 750),
            ))
          : null,
      })),
    );
  };

  return (
    <section className="curve-calibration" aria-labelledby="curve-calibration-title">
      <div className="curve-section-heading">
        <div>
          <p className="eyebrow">Step 2 · Calibration</p>
          <h3 id="curve-calibration-title">Calibrate axes from printed ticks</h3>
        </div>
        {activeTrace === null ? null : (
          <label className="curve-inline-check">
            <input {...guided(curveImportFieldHelp.traceCalibration, activeTrace?.id ?? 'shared')}
              checked={usesOverride}
              type="checkbox"
              onChange={(event) => toggleOverride(event.target.checked)}
            />
            Use trace-specific panel calibration
          </label>
        )}
      </div>
      <div className="curve-calibration-guide" role="note">
        <p>
          <strong>How to calibrate:</strong> choose <em>Inspect calibration pixels</em>{' '}
          above, click the center of two labeled ticks on the flow axis, and pair
          each x pixel with its printed value. Repeat with two y pixels on the
          vertical axis. Enter the units exactly as printed.
        </p>
        <p>
          First and second pixels are anchors, not chart corners. Direction describes
          whether the printed value rises or falls from the first anchor to the
          second. Current scope:{' '}
          <strong>
            {usesOverride && activeTrace !== null
              ? `trace-specific for ${activeTrace.label}`
              : 'shared chart calibration'}
          </strong>
          .
        </p>
      </div>
      {calibration === null ? (
        <div className="curve-empty">
          <p>
            No calibration exists yet. Create one, then replace every default anchor with pixels and printed values you inspect on this source.
          </p>
          <button type="button" onClick={createCalibration}>
            Create calibration
          </button>
        </div>
      ) : (
        <div className="curve-calibration__grid">
          <AxisEditor
            axis={calibration.x_axis}
            axisName="Flow axis"
            lockQuantity
            pixelCoordinate="x"
            onChange={(xAxis) =>
              commitCalibration({ ...calibration, x_axis: xAxis })
            }
          />
          <AxisEditor
            axis={calibration.y_axis}
            axisName="Vertical axis"
            pixelCoordinate="y"
            onChange={(yAxis) =>
              commitCalibration({ ...calibration, y_axis: yAxis })
            }
          />
        </div>
      )}
    </section>
  );
}
